'use client'

import * as Sentry from '@sentry/nextjs'
import confetti from 'canvas-confetti'
import { Frown, HeartCrack, PartyPopper, Smile, SmilePlus } from 'lucide-react'
import * as React from 'react'

import { AuthModal } from '@/components/ui/auth-modal'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

type FeedbackEmotion = 'crying' | 'sad' | 'normal' | 'happy'

// Store uppercase topic codes in DB.
type FeedbackTopicCode = 'AI' | 'ACCOUNT' | 'API_SDK' | 'INTERFACE' | 'MODELS' | 'WORKFLOWS' | 'JOBS' | 'OTHER'

const TOPICS: Array<{ value: FeedbackTopicCode; label: string }> = [
  { value: 'AI', label: 'AI (Agent, Tool Calls)' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'API_SDK', label: 'API - SDK' },
  { value: 'INTERFACE', label: 'Interface (Navigation, UI Issues)' },
  { value: 'MODELS', label: 'Models' },
  { value: 'WORKFLOWS', label: 'Workflows' },
  { value: 'JOBS', label: 'Jobs (Performance, Failure)' },
  { value: 'OTHER', label: 'Other' },
]

type FeedbackDialogProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}

export function FeedbackDialog({ open: openProp, onOpenChange, hideTrigger = false }: FeedbackDialogProps = {}) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [authOpen, setAuthOpen] = React.useState(false)
  const [topic, setTopic] = React.useState<FeedbackTopicCode | ''>('')
  const [textFeedback, setTextFeedback] = React.useState('')
  const [emotion, setEmotion] = React.useState<FeedbackEmotion | ''>('')
  const [submitting, setSubmitting] = React.useState(false)
  const open = openProp ?? internalOpen

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange]
  )

  const canSubmit = Boolean(topic) && Boolean(textFeedback.trim()) && Boolean(emotion) && !submitting

  const reset = React.useCallback(() => {
    setTopic('')
    setTextFeedback('')
    setEmotion('')
  }, [])

  const fireCoffeeConfetti = React.useCallback(() => {
    const c: any = confetti as any
    try {
      const colors = ['#ef4444', '#3b82f6', '#f59e0b', '#22c55e', '#a855f7']
      const coffeeShape = typeof c.shapeFromText === 'function' ? c.shapeFromText({ text: '☕️', scalar: 2 }) : null
      if (coffeeShape) {
        c({
          particleCount: 18,
          spread: 70,
          startVelocity: 25,
          origin: { y: 0.7 },
          shapes: [coffeeShape],
          colors,
        })
        c({
          particleCount: 10,
          spread: 90,
          startVelocity: 18,
          origin: { y: 0.7 },
          shapes: [coffeeShape],
          colors,
        })
        // Add a small burst of standard confetti too, to guarantee colorful output
        // even if the emoji shape renders monochrome on some browsers.
        c({
          particleCount: 40,
          spread: 75,
          startVelocity: 28,
          origin: { y: 0.7 },
          colors,
        })
      } else {
        c({
          particleCount: 70,
          spread: 75,
          startVelocity: 28,
          origin: { y: 0.7 },
          colors,
        })
      }
    } catch {
      // ignore
    }
  }, [])

  const onSubmit = async () => {
    const API_URL = process.env.NEXT_PUBLIC_API_URL
    if (!API_URL) {
      toast({ title: 'Feedback not configured', description: 'Missing NEXT_PUBLIC_API_URL.' })
      return
    }
    if (!topic || !textFeedback.trim()) return

    setSubmitting(true)
    try {
      const res = await fetch(`${API_URL.replace(/\/$/, '')}/v1/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic,
          text_feedback: textFeedback.trim(),
          emotion,
        }),
      })

      if (res.status === 401) {
        setAuthOpen(true)
        return
      }
      if (!res.ok) {
        throw new Error(`Failed to send feedback (${res.status})`)
      }

      try {
        if (typeof Sentry.metrics?.count === 'function') {
          Sentry.metrics.count('user_feedback', 1, {
            attributes: {
              topic: topic as string,
              reaction: emotion as string,
            },
          })
          void Sentry.flush(2000)
        }
      } catch (metricErr) {
        console.error('[Sentry] Failed to send user_feedback metric', metricErr)
      }

      toast({ title: 'Thanks!', description: 'Your feedback was sent.' })
      setOpen(false)
      reset()
    } catch (e) {
      toast({
        title: 'Could not send feedback',
        description: String((e as any)?.message || 'Unknown error'),
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v)
          if (!v) reset()
        }}
      >
        {!hideTrigger ? (
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center gap-2">
              <SmilePlus className="h-4 w-4" />
              Give Feedback
            </Button>
          </DialogTrigger>
        ) : null}

        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Feedback</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Select value={topic} onValueChange={(v) => setTopic(v as FeedbackTopicCode)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a topic..." />
              </SelectTrigger>
              <SelectContent>
                {TOPICS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Textarea
              placeholder="Your feedback..."
              value={textFeedback}
              onChange={(e) => setTextFeedback(e.target.value)}
              className="min-h-[140px]"
            />

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {(
                  [
                    { key: 'crying', icon: HeartCrack, label: 'Crying' },
                    { key: 'sad', icon: Frown, label: 'Sad' },
                    { key: 'normal', icon: Smile, label: 'Normal' },
                    { key: 'happy', icon: PartyPopper, label: 'Happy' },
                  ] as const
                ).map(({ key, icon: Icon, label }) => (
                  <Button
                    key={key}
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={label}
                    title={label}
                    onClick={() => {
                      setEmotion(key)
                      if (key === 'happy') fireCoffeeConfetti()
                    }}
                    className={cn('h-9 w-9 rounded-full', emotion === key && 'bg-muted ring-1 ring-border')}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>
                ))}
              </div>

              <DialogFooter className="sm:justify-end">
                <Button onClick={onSubmit} disabled={!canSubmit}>
                  {submitting ? 'Sending…' : 'Send'}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

